import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import Dotdotdot from 'react-dotdotdot'
import ProgressBar from 'components/progress-bar/ProgressBar'
import Icon from 'components/icon/Icon'
import Price from './Price/Price'
import { getCapacityState, getEnergyNameI18Key, isReserved } from 'utils/producers'
import { CardProps as Props } from './types'
import EnergyTypeIcon from '../energy-type-icon/EnergyTypeIcon'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from 'store/types'
import classNames from 'classnames'
import { useRouter } from 'next/router'
import styles from './ProducerCard.module.scss'
import { routes } from 'utils/routes'
import Button from 'components/button/Button'
import { ButtonTypes } from 'components/button/types'
import { SimulationType } from 'store/marketing/types'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { setSelectedProducer } from 'store/app/actions/setSelectedProducer'

const ProducerCard: FC<Props> = ({
  producer,
  onMouseEnter,
  onMouseLeave,
  isActive,
  isSelected,
  lineClamp,
  showDescription,
  showCTA,
  showInjectionPrice,
  showReadMore,
  handleClickWhenSelectable,
  hidePrice
}) => {
  // REDUX STORE
  const { marketing, simulation } = useSelector((store: Store) => store)
  const dispatch = useDispatch()

  // Next Router
  const router = useRouter()
  const { locale } = router

  // i18n
  const { t } = useTranslation(['producer', 'producers'])

  // Constants
  const capacityState = getCapacityState(producer, simulation)
  const didSimulation = simulation.didSimulation || simulation.isLoading
  const isSelectable = typeof isSelected !== 'undefined'

  if (marketing && marketing.leadSource === 'offline') showDescription = false

  /**
   * Returns the CTA component based on state & didSimulation
   *
   * @returns HTMLElement|null
   */
  const getCta = () => {
    if (showCTA) {
      if (isReserved(producer)) {
        return (
          <Button isFullWidth isDisabled withGradient>
            {t('reserved', 'Gereserveerd')}
          </Button>
        )
      }

      if (capacityState.soldOut) {
        return (
          <Button isFullWidth isDisabled withGradient>
            {t('soldOut', 'Uitverkocht')}
          </Button>
        )
      }

      if (didSimulation || marketing.simulationType !== SimulationType.PRICING_SIMULATION) {
        return (
          <Button
            onClick={(e) => {
              e.stopPropagation()
              if (didSimulation) {
                dispatch(setSelectedProducer({ producer }))
                router.push(routes(locale, producer.slug).registration)
              } else {
                dispatch(showSimulation())
              }
            }}
            isFullWidth
            isDisabled={simulation.isLoading}
            withGradient
          >
            {t('subscribe', 'Aanmelden')}
          </Button>
        )
      }

      return (
        <Button
          onClick={(e) => {
            e.stopPropagation()
            router.push(routes(locale, producer.slug).producerInfo)
          }}
          isFullWidth
          isDisabled={simulation.isLoading}
          withGradient
        >
          {t('readMoreCTA', 'Lees meer')}
        </Button>
      )
    }

    return null
  }

  /**
   * Returns the state based on the capacity state & popularity
   *
   * @returns HTMLElement|null
   */
  const getState = () => {
    if (isReserved(producer)) {
      return <Button extraClasses={styles.reserved}>{t('reserved', 'Gereserveerd')}</Button>
    }

    if (capacityState.soldOut) {
      return (
        <Button extraClasses={styles['sold-out']} type={ButtonTypes.TERNARY} isInverse withGradient>
          {t('soldOut', 'Uitverkocht')}
        </Button>
      )
    }

    if (capacityState.almostSoldOut) {
      return (
        <Button extraClasses={styles['sold-out']} type={ButtonTypes.TERNARY} isInverse withGradient>
          {t('almostSoldOut', 'Bijna uitverkocht')}
        </Button>
      )
    }

    if (producer.isPopular[locale]) {
      return (
        <Button extraClasses={styles['popular']} type={ButtonTypes.TERNARY} isInverse withGradient>
          {t('isPopular', 'Populair')}
        </Button>
      )
    }

    return null
  }

  return (
    <div
      role="presentation"
      className={classNames(styles['producer-card'], styles.inner, {
        [styles['is-active']]: isActive || producer.isPopular[locale],
        [styles['is-selectable']]: isSelectable,
        [styles['is-selected']]: isSelected
      })}
      onClick={() => {
        if (isSelectable) {
          handleClickWhenSelectable()
        } else {
          router.push(routes(locale, producer.slug).producerInfo)
        }
      }}
      onMouseEnter={() => onMouseEnter && onMouseEnter(producer)}
      onMouseLeave={() => onMouseLeave && onMouseLeave()}
    >
      <div className={styles.inner}>
        <div className={styles.photo} style={{ backgroundImage: `url(${producer.image})` }}>
          {!isSelectable && (
            <Button
              onClick={(e) => {
                e.stopPropagation()
                router.push(routes(locale, producer.slug).producerInfo)
              }}
              extraClasses={styles['read-more']}
              withGradient
            >
              {t('readMore', 'Bekijk producent')}
            </Button>
          )}
          {getState()}
          {!hidePrice &&
            marketing.simulationType === SimulationType.PRICING_SIMULATION &&
            (simulation.didSimulation || simulation.isLoading) && <Price showInjectionPrice={showInjectionPrice} />}
        </div>
        <div className={styles['producer-content']}>
          <div className={classNames('is-flex-row', styles.details)}>
            <EnergyTypeIcon energyType={producer.energyType} />
            <span className={styles.energy}>{t(`producers:${getEnergyNameI18Key(producer.energyType)}`)}</span>
            <span className={styles.location}>{producer.location}</span>
          </div>
          <h3
            className={classNames(
              'font-size-xl',
              'font-weight-bold',
              'text-overflow-ellipsis',
              { 'margin-bottom-s': showDescription },
              { 'margin-bottom-m': !showDescription }
            )}
          >
            {producer.name[locale]}
          </h3>
          {showDescription && (
            <div className={classNames(styles.description, `margin-bottom-${showReadMore ? 'm' : 'l'}`)}>
              <Dotdotdot clamp={lineClamp}>{producer.description[locale]}</Dotdotdot>
            </div>
          )}

          {showReadMore && (
            <Button
              extraClasses={styles['read-more-link']}
              onClick={(e) => {
                e.stopPropagation()
                router.push(routes(locale, producer.slug).producerInfo)
              }}
            >
              <span>{t('readMore', 'Meer info')}</span>
            </Button>
          )}

          <p className={classNames('font-size-s', 'font-weight-regular', 'margin-top-none', 'margin-bottom-xs', styles.percentage)}>
            {t('alreadyOrdered', 'Reeds besteld')}: {capacityState.usedEnergy}%
          </p>
          <ProgressBar className={classNames({ 'margin-bottom-m': showCTA })} current={capacityState.usedEnergy} />
          {getCta()}
        </div>
      </div>
      {isSelectable && isSelected && (
        <div className={styles.check}>
          <Icon className={styles['selected-icon']} name="check-jagged" size={30} color="#23d5ae" />
          <span className={styles['selected-text']}>{t('selected', 'Geselecteerd')}</span>
        </div>
      )}
      {isSelectable && !isSelected && (
        <div className={styles.check}>
          <div className={styles.placeholder} />
        </div>
      )}
    </div>
  )
}

ProducerCard.defaultProps = {
  showDescription: true,
  showCTA: false,
  showInjectionPrice: false,
  showReadMore: false,
  lineClamp: 4
}

export default ProducerCard
